import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { type CustomTabId } from './customTabIds';
import { type PageTab, type TabKey } from './tabKey';
import { logout } from '../session/logoutThunk';
import type { RootState } from '../store';

type AwardLetterTab = Readonly<{ userCollegeId: number; name: string }>;

type TabsState = Readonly<{
  openCustomTabs: CustomTabId[];
  selectedTabKey: TabKey | null;
  pageTabs: PageTab[];
  awardLetter: AwardLetterTab | null;
}>;

const initialState: TabsState = {
  openCustomTabs: [],
  selectedTabKey: null,
  pageTabs: [],
  awardLetter: null,
};

export const tabsSlice = createSlice({
  name: 'tabs',
  initialState,
  reducers: {
    openCustomTab: (state, action: PayloadAction<CustomTabId>) => {
      const id = action.payload;

      if (!state.openCustomTabs.includes(id)) {
        state.openCustomTabs.push(id);
      }

      state.selectedTabKey = `custom:${id}`;
    },
    openAwardLetterTab: (state, action: PayloadAction<AwardLetterTab>) => {
      state.awardLetter = action.payload;

      if (!state.openCustomTabs.includes('awardLetter')) {
        state.openCustomTabs.push('awardLetter');
      }

      state.selectedTabKey = 'custom:awardLetter';
    },
    closeCustomTab: (state, action: PayloadAction<CustomTabId>) => {
      state.openCustomTabs = state.openCustomTabs.filter((id) => id !== action.payload);

      if (action.payload === 'awardLetter') state.awardLetter = null;
    },
    setSelectedTabKey: (state, action: PayloadAction<TabKey | null>) => {
      state.selectedTabKey = action.payload;
    },
    setPageTabs: (state, action: PayloadAction<readonly PageTab[]>) => {
      state.pageTabs = [...action.payload];
    },
    clearPageTabs: (state) => {
      state.pageTabs = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState);
  },
});

export const {
  openCustomTab,
  openAwardLetterTab,
  closeCustomTab,
  setSelectedTabKey,
  setPageTabs,
  clearPageTabs,
} = tabsSlice.actions;

export const selectPageTabs = (state: RootState): readonly PageTab[] => state.tabs.pageTabs;

export const selectOpenCustomTabs = (state: RootState): readonly CustomTabId[] =>
  state.tabs.openCustomTabs;

export const selectSelectedTabKey = (state: RootState): TabKey | null => state.tabs.selectedTabKey;

export const selectAwardLetterTab = (state: RootState): AwardLetterTab | null =>
  state.tabs.awardLetter;
